import pMap from 'p-map';
import { SetStatus } from '@/Enums/enum';
import { supabase } from '@/lib/supabase';
import type { SubmissionWithCourse } from '@/lib/types';
import { removeCommentTags } from '@/lib/utils';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

const CONCURRENT_APPROVALS = 5;

/** Approve a single course submission via the publish_course_draft RPC. */
async function approveCourseSubmission(courseId: string) {
	const { data: courseData, error: courseError } = await supabase
		.from('courses')
		.select(
			`
			course_draft,
			submission:submissions!courses_submission_id_fkey (
				admin_approval,
				dept_approval
			)
		`
		)
		.eq('course_id', courseId)
		.eq('school_id', schoolId)
		.maybeSingle();

	if (courseError) throw courseError;
	if (!courseData) return;

	const submission = courseData.submission as {
		admin_approval: string;
		dept_approval: string;
	} | null;
	if (
		submission?.admin_approval === SetStatus.APPROVED &&
		submission?.dept_approval === SetStatus.APPROVED
	) {
		return;
	}

	const draftCourse = JSON.parse(removeCommentTags(courseData.course_draft ?? '{}'));

	const { error: rpcError } = await supabase.rpc('publish_course_draft', {
		p_course_id: courseId,
		p_school_id: schoolId,
		p_draft: draftCourse,
	});

	if (rpcError) throw rpcError;

	const { error: approvalError } = await supabase
		.from('submissions')
		.update({
			admin_approval: SetStatus.APPROVED,
			dept_approval: SetStatus.APPROVED,
		})
		.eq('course_id', courseId);

	if (approvalError) throw approvalError;
}

/** Mass-approve all pending submissions with bounded concurrency. */
export async function massApproveSubmissions(
	querySubmissionData: SubmissionWithCourse[] | undefined
) {
	if (!querySubmissionData) return;

	const courseIds = querySubmissionData
		.map(({ course }) => course?.course_id)
		.filter((id): id is string => !!id);

	await pMap(courseIds, approveCourseSubmission, { concurrency: CONCURRENT_APPROVALS });
}
